import React, { useState } from 'react';
import './StrategyRoadmap.css';

/**
 * Visual phase roadmap. Renders the strategy's phases as a horizontal
 * track of connected nodes (vertical on narrow viewports), highlighting
 * the current phase and letting the user click any node to expand its
 * details.
 *
 * @param {Object} props
 * @param {Array}  props.phases             - Array of {name, weeks, training_focus, milestone}
 * @param {number} props.currentPhaseIndex  - 0-based index of the current phase, or -1 if not active
 * @param {number} [props.weeksElapsed]     - Total weeks elapsed in the strategy
 */
function StrategyRoadmap({ phases, currentPhaseIndex, weeksElapsed }) {
    const [expandedIdx, setExpandedIdx] = useState(currentPhaseIndex >= 0 ? currentPhaseIndex : null);

    if (!phases || phases.length === 0) return null;

    const handleToggle = (idx) => {
        setExpandedIdx(prev => (prev === idx ? null : idx));
    };

    // Compute weeks-remaining-in-current-phase to show under the active node
    let weeksRemaining = null;
    if (currentPhaseIndex >= 0 && weeksElapsed != null) {
        const current = phases[currentPhaseIndex];
        const match = (current?.weeks || '').match(/(\d+)\s*-\s*(\d+)/);
        if (match) {
            const endWeek = parseInt(match[2], 10);
            weeksRemaining = Math.max(0, endWeek - weeksElapsed);
        }
    }

    return (
        <div className="strategy-roadmap">
            <div className="roadmap-track">
                {phases.map((phase, i) => {
                    const isCurrent = i === currentPhaseIndex;
                    const isDone = currentPhaseIndex >= 0 && i < currentPhaseIndex;
                    const isExpanded = expandedIdx === i;
                    const stateClass = isCurrent ? 'current' : isDone ? 'done' : 'future';

                    return (
                        <React.Fragment key={i}>
                            <button
                                type="button"
                                className={`roadmap-phase ${stateClass} ${isExpanded ? 'expanded' : ''}`}
                                onClick={() => handleToggle(i)}
                                aria-expanded={isExpanded}
                            >
                                <div className="roadmap-node">
                                    <span className="roadmap-node-icon">
                                        {isDone ? '✓' : isCurrent ? '●' : i + 1}
                                    </span>
                                </div>
                                <div className="roadmap-phase-label">
                                    <div className="roadmap-phase-name">{phase.name}</div>
                                    {phase.weeks && (
                                        <div className="roadmap-phase-weeks">
                                            Wk {phase.weeks}
                                            {isCurrent && weeksRemaining !== null && (
                                                <span className="roadmap-remaining">
                                                    {' · '}
                                                    {weeksRemaining === 0
                                                        ? 'final week'
                                                        : `${weeksRemaining}w left`}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </button>
                            {i < phases.length - 1 && (
                                <div className={`roadmap-connector ${isDone ? 'done' : ''}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>

            {expandedIdx !== null && phases[expandedIdx] && (
                <div className="roadmap-details">
                    <div className="roadmap-details-header">
                        <span className="roadmap-details-name">{phases[expandedIdx].name}</span>
                        {phases[expandedIdx].weeks && (
                            <span className="roadmap-details-weeks">Weeks {phases[expandedIdx].weeks}</span>
                        )}
                    </div>
                    {phases[expandedIdx].training_focus && (
                        <div className="roadmap-details-focus">
                            <span className="roadmap-details-label">Focus:</span> {phases[expandedIdx].training_focus}
                        </div>
                    )}
                    {phases[expandedIdx].milestone && (
                        <div className="roadmap-details-milestone">
                            🎯 {phases[expandedIdx].milestone}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default StrategyRoadmap;
